import {useQuery} from '@apollo/client';
import {Spin} from 'antd';
import moment from 'moment';
import shell from 'pages/AssetManagement/v2/pageShell.module.scss';
import {GET_EXCHANGE_MARKET} from './resolvers';
import {ExchangeScopeSelector} from './ExchangeScopeSelector';
import {IndexChart} from './IndexChart';
import {useExchangeScope} from './useExchangeScope';
import styles from './exchanges.module.scss';

/** A polyline over the value range, padded so a flat series still draws. */
const sparkPath = (values, width, height) => {
  const points = (values || []).filter(value => Number.isFinite(value));
  if (points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  return points
    .map((value, index) => {
      const x = (index / (points.length - 1)) * width;
      const y = height - ((value - min) / span) * (height - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
};

const changeClass = value => {
  if (value == null) return styles.perfFlat;
  if (value > 0) return styles.perfUp;
  if (value < 0) return styles.perfDown;
  return styles.perfFlat;
};

const signed = value =>
  value == null ? '—' : `${value > 0 ? '+' : ''}${Number(value).toFixed(2)}%`;

/**
 * The movers fields return the FinancialInstrument union and the query only
 * asks for PublicEquity, so an exchange whose movers are bonds or funds yields
 * `{}` for each row. Those are not instruments; drop them.
 */
const realRows = rows => (rows || []).filter(row => row?.id);

const Sparkline = ({values, positive}) => {
  const path = sparkPath(values, 260, 40);
  if (!path) return null;
  return (
    <svg
      className={styles.spark}
      viewBox="0 0 260 40"
      preserveAspectRatio="none"
      aria-hidden="true">
      <polyline
        points={path}
        fill="none"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        // preserveAspectRatio="none" stretches the geometry, which also
        // stretched the stroke — thicker on wide screens than narrow ones.
        vectorEffect="non-scaling-stroke"
        stroke={positive ? '#12805c' : '#bf3320'}
      />
    </svg>
  );
};

const VenueCard = ({node, onOpen}) => {
  const {exchange, instrumentCount, performanceToday} = node;
  const change = performanceToday?.changePercent;

  return (
    <div
      role="button"
      tabIndex={0}
      className={styles.venueCard}
      onClick={() => onOpen(exchange.id)}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(exchange.id);
        }
      }}>
      <div className={styles.venueHead}>
        <div>
          <div className={styles.venueCode}>{exchange.code}</div>
          <div className={styles.venueName}>{exchange.stockExchangeName}</div>
        </div>
        {exchange.logoImage && (
          <img className={styles.venueLogo} src={exchange.logoImage} alt="" />
        )}
      </div>

      {performanceToday?.changePercent == null ? (
        <div className={styles.perfNone}>
          No market data published for this exchange today.
        </div>
      ) : (
        <>
          <div className={styles.perf}>
            <span className={`${styles.perfValue} ${changeClass(change)}`}>
              {signed(change)}
            </span>
            {performanceToday.changeAbsolute != null && (
              <span className={styles.perfAbs}>
                {performanceToday.changeAbsolute > 0 ? '+' : ''}
                {Number(performanceToday.changeAbsolute).toLocaleString()}
                {performanceToday.asOf
                  ? ` · ${moment(performanceToday.asOf).format('HH:mm')}`
                  : ''}
              </span>
            )}
          </div>
          <Sparkline values={performanceToday.spark} positive={change >= 0} />
        </>
      )}

      <div className={styles.venueStats}>
        <div className={styles.stat}>
          <div className={styles.statLabel}>Live</div>
          <div className={styles.statValue}>
            {instrumentCount?.toLocaleString() ?? '—'}
          </div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>Listings</div>
          <div className={styles.statValue}>
            {exchange.numberOfListings?.toLocaleString() ?? '—'}
          </div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>Settles in</div>
          <div className={styles.statValue}>
            {exchange.estimatedSettlementDays != null
              ? `${exchange.estimatedSettlementDays}d`
              : '—'}
          </div>
        </div>
      </div>

      {/* An ops person reading a stuck queue needs to know whether the exchange
          even accepts orders. */}
      <div className={styles.venueFoot}>
        <span
          className={`${styles.flag} ${
            exchange.tradeable ? styles.flagOn : styles.flagOff
          }`}>
          {exchange.tradeable ? 'Tradeable' : 'Not tradeable'}
        </span>
        {exchange.enableLimitOrder && (
          <span className={styles.flag}>Limit</span>
        )}
        {exchange.enableMarketOrder && (
          <span className={styles.flag}>Market</span>
        )}
        {exchange.orderProcessingCutoffTime && (
          <span className={styles.flag}>
            Cutoff {exchange.orderProcessingCutoffTime}
          </span>
        )}
      </div>
    </div>
  );
};

const MoverPanel = ({title, subtitle, rows, render}) => (
  <div className={styles.panel}>
    <div className={styles.panelTitle}>{title}</div>
    <div className={styles.panelSub}>{subtitle}</div>
    {rows.length ? (
      <div className={styles.moverList}>{rows.map(render)}</div>
    ) : (
      <div className={styles.empty}>Nothing to show for this exchange.</div>
    )}
  </div>
);

/**
 * Trading View → Market Overview.
 *
 * Read-only. Everything here is server-derived and was previously invisible in
 * admin: an ops person deciding whether an order queue is stuck could not see
 * whether the market was even open.
 *
 * Only BRVM and NGX publish performance today; DABA and NSE return null, so
 * "no data" is a normal state rather than an error.
 */
export const MarketOverview = () => {
  const scope = useExchangeScope();
  const {exchangeId} = scope;

  const {data, loading} = useQuery(GET_EXCHANGE_MARKET, {
    variables: {
      input: {
        ...(exchangeId ? {exchangeId: [exchangeId]} : {}),
        language: 'EN',
        moversLimit: 5,
        sectorLimit: 8,
        includeSectors: true,
        includeCompositeIndex: true,
        indexRange: 'ONE_MONTH',
      },
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const nodes = data?.getExchanges?.data ?? [];
  const venues = nodes.filter(node => node.exchange);
  const aggregate = nodes.find(node => !node.exchange);
  const single = exchangeId ? venues[0] : null;

  return (
    <div>
      <div className={shell.head}>
        <div className={shell.headTop}>
          <div>
            <h1 className={shell.title}>Market Overview</h1>
            <div className={shell.sub}>
              Live exchange state — performance, index, movers. Read-only.
            </div>
          </div>
        </div>
        <ExchangeScopeSelector {...scope} />
      </div>

      {loading && !data ? (
        <div className="query-loading">
          <Spin size="large" />
          <h1>Loading…</h1>
        </div>
      ) : single ? (
        <div className={styles.detailGrid}>
          {/* Was a column of bare divs with `style={{height: 16}}` spacers
              between them. */}
          <div className={styles.stack}>
            <div className={styles.panel}>
              <div className={styles.panelTitle}>
                {single.exchange.code} composite index
              </div>
              <div className={styles.panelSub}>
                {single.compositeIndex?.asOf
                  ? `As of ${moment(single.compositeIndex.asOf).format(
                      'DD MMM YYYY, HH:mm',
                    )}`
                  : 'Last month'}
              </div>
              {single.compositeIndex?.value == null ? (
                <div className={styles.empty}>
                  This exchange does not publish a composite index.
                </div>
              ) : (
                <>
                  <div className={styles.indexValue}>
                    {Number(single.compositeIndex.value).toLocaleString()}
                    <span
                      className={`${styles.perfAbs} ${changeClass(
                        single.compositeIndex.changePercent,
                      )}`}>
                      {' '}
                      {signed(single.compositeIndex.changePercent)}
                    </span>
                  </div>
                  <IndexChart series={single.compositeIndex.series} />
                </>
              )}
            </div>

            <div className={styles.panel}>
              <div className={styles.panelTitle}>Sector performance</div>
              <div className={styles.panelSub}>
                Change today, with the number of instruments behind each sector
              </div>
              {(single.sectorPerformance || []).length ? (
                (() => {
                  const rows = single.sectorPerformance;
                  // Was `Math.abs(value) * 12` — an arbitrary scale on which
                  // anything past 8.3% pinned to full width and everything
                  // below 1% was invisible. Relative to the largest move in
                  // the set, the bars actually compare to each other.
                  const peak = Math.max(
                    ...rows.map(row => Math.abs(row.changePercent ?? 0)),
                    0.01,
                  );
                  return rows.map(row => {
                    const value = row.changePercent ?? 0;
                    const width = Math.max((Math.abs(value) / peak) * 100, 2);
                    return (
                      <div
                        className={styles.sectorRow}
                        key={
                          row.sector?.id ?? row.sector?.name ?? 'unclassified'
                        }>
                        <span className={styles.sectorName}>
                          {/* A sector with no name rendered as a bare dash,
                              which read as missing data rather than a real
                              bucket of instruments. */}
                          {row.sector?.name || 'Unclassified'}
                        </span>
                        <span
                          className={styles.sectorCount}
                          title={`${row.constituentCount ?? 0} instruments`}>
                          {row.constituentCount ?? 0}
                        </span>
                        <span className={styles.sectorBar}>
                          <span
                            style={{
                              width: `${width}%`,
                              background: value >= 0 ? '#12805c' : '#bf3320',
                            }}
                          />
                        </span>
                        <span
                          className={`${styles.sectorValue} ${changeClass(
                            value,
                          )}`}>
                          {signed(row.changePercent)}
                        </span>
                      </div>
                    );
                  });
                })()
              ) : (
                <div className={styles.empty}>
                  No sector data for this exchange.
                </div>
              )}
            </div>
          </div>

          <div className={styles.stack}>
            <MoverPanel
              title="Gainers"
              subtitle="Top movers up today"
              rows={realRows(single.gainers)}
              render={row => (
                <div className={styles.moverRow} key={row.id}>
                  <span className={styles.moverName}>
                    <span className={styles.moverTicker}>{row.ticker}</span>
                    {row.name}
                  </span>
                  <span
                    className={`${styles.moverValue} ${changeClass(
                      row.pricing?.changePercent,
                    )}`}>
                    {signed(row.pricing?.changePercent)}
                  </span>
                </div>
              )}
            />
            <MoverPanel
              title="Losers"
              subtitle="Top movers down today"
              rows={realRows(single.losers)}
              render={row => (
                <div className={styles.moverRow} key={row.id}>
                  <span className={styles.moverName}>
                    <span className={styles.moverTicker}>{row.ticker}</span>
                    {row.name}
                  </span>
                  <span
                    className={`${styles.moverValue} ${changeClass(
                      row.pricing?.changePercent,
                    )}`}>
                    {signed(row.pricing?.changePercent)}
                  </span>
                </div>
              )}
            />
            <MoverPanel
              title="Most traded"
              subtitle="By volume today"
              rows={realRows(single.mostTraded)}
              render={row => (
                <div className={styles.moverRow} key={row.id}>
                  <span className={styles.moverName}>
                    <span className={styles.moverTicker}>{row.ticker}</span>
                    {row.name}
                  </span>
                  <span className={`${styles.moverValue} ${styles.perfFlat}`}>
                    {row.tradingVolume?.toLocaleString() ?? '—'}
                  </span>
                </div>
              )}
            />
          </div>
        </div>
      ) : (
        <>
          {aggregate && (
            <div className={styles.panel} style={{marginBottom: 16}}>
              <div className={styles.panelTitle}>All markets</div>
              <div className={styles.panelSub}>
                Blended across every exchange
              </div>
              <div className={styles.perf}>
                <span
                  className={`${styles.perfValue} ${changeClass(
                    aggregate.performanceToday?.changePercent,
                  )}`}>
                  {signed(aggregate.performanceToday?.changePercent)}
                </span>
                <span className={styles.perfAbs}>
                  {aggregate.instrumentCount?.toLocaleString()} live instruments
                  across {venues.length} exchanges
                </span>
              </div>
            </div>
          )}
          <div className={styles.venueGrid}>
            {venues.map(node => (
              <VenueCard
                key={node.exchange.id}
                node={node}
                onOpen={scope.setScope}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MarketOverview;
